import type { AttachmentInput } from '@moussenaires/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { feedApi } from '../api/feed.js';
import { ApiClientError } from '../api/client.js';
import { UploadDropzone, type PendingAttachment } from './UploadDropzone.js';
import './PostComposer.css';

export function PostComposer({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [tags, setTags] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkField, setShowLinkField] = useState(false);
  const [uploads, setUploads] = useState<PendingAttachment[]>([]);
  const [links, setLinks] = useState<string[]>([]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const attachments: AttachmentInput[] = [
        ...uploads.map((upload) => ({ mediaId: upload.media.id })),
        ...links.map((url) => ({ url })),
      ];
      return feedApi.create({
        title: title.trim(),
        body: body.trim() || undefined,
        tags: tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        attachments,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
      onDone();
    },
  });

  const errorMessage =
    createMutation.error instanceof ApiClientError
      ? t(`errors.${createMutation.error.code}`)
      : null;

  return (
    <div className="post-composer card">
      <input
        className="post-composer-title"
        placeholder={t('feed.postTitle')}
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        maxLength={140}
        autoFocus
      />
      <textarea
        className="post-composer-body"
        placeholder={t('feed.postBody')}
        rows={3}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        maxLength={8000}
      />
      <input
        className="post-composer-tags"
        placeholder={`${t('feed.tags')} (${t('feed.tagsHint')})`}
        value={tags}
        onChange={(event) => setTags(event.target.value)}
      />

      <UploadDropzone
        attachments={uploads}
        onAdd={(attachment) => setUploads((current) => [...current, attachment])}
        onRemove={(key) => setUploads((current) => current.filter((item) => item.key !== key))}
        disabled={createMutation.isPending}
      />

      {links.length > 0 && (
        <ul className="post-composer-links">
          {links.map((url) => (
            <li key={url}>
              <Link2 size={13} />
              <span>{url}</span>
              <button
                type="button"
                onClick={() => setLinks((current) => current.filter((item) => item !== url))}
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {showLinkField ? (
        <div className="post-composer-link-input">
          <input
            placeholder="https://…"
            value={linkUrl}
            onChange={(event) => setLinkUrl(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && linkUrl.trim()) {
                event.preventDefault();
                setLinks((current) => [...current, linkUrl.trim()]);
                setLinkUrl('');
                setShowLinkField(false);
              }
            }}
            autoFocus
          />
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              if (linkUrl.trim()) setLinks((current) => [...current, linkUrl.trim()]);
              setLinkUrl('');
              setShowLinkField(false);
            }}
          >
            {t('common.confirm')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn-ghost post-composer-add-link"
          onClick={() => setShowLinkField(true)}
        >
          <Link2 size={14} /> {t('feed.addLink')}
        </button>
      )}

      {errorMessage && <p className="field-error">{errorMessage}</p>}

      <div className="post-composer-footer">
        <button type="button" className="btn" onClick={onDone}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!title.trim() || createMutation.isPending}
          onClick={() => createMutation.mutate()}
        >
          {t('feed.publish')}
        </button>
      </div>
    </div>
  );
}
